import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai/index";
import classNames from "classnames";

import { NamedInput } from "components/NamedInput/NamedInput";
import { NamedSelect } from "components/NamedSelect/NamedSelect";
import { Option } from "components/Select/Select";
import { Divider } from "components/Divider/Divider";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { useItemSearchOptions } from "hooks/useItemSearchOptions";

import {
  openedDocItemStateAtom,
  openedDocumentAtom,
} from "../../../atoms/documentsAtom";
import { allItemsAtom } from "../../../atoms/itemAtom";
import { measureUnitsAtom } from "../../../atoms/measureUnitsAtom";
import { allPriceListsAtom } from "../../../atoms/priceListAtom";

import styles from "../styles.module.scss";
interface ComponentProps {}

const ElementContainer = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  return <div className={styles.componentContainer}>{children}</div>;
};

export const DocItemModal4: FC<ComponentProps> = ({}): JSX.Element => {
  const [allItems] = useAtom(allItemsAtom);
  const [measureUnits] = useAtom(measureUnitsAtom);
  const [openedDocItemState, setOpenedDocumentState] = useAtom(
    openedDocItemStateAtom
  );

  const [itemSearch1, setItemSearch1] = useState("");
  const [itemSearch2, setItemSearch2] = useState("");
  const {
    items: remoteItems1,
    options: remoteItemOptions1,
    isLoading: isItemsLoading1,
  } = useItemSearchOptions(itemSearch1, openedDocItemState.itemid);
  const {
    items: remoteItems2,
    options: remoteItemOptions2,
    isLoading: isItemsLoading2,
  } = useItemSearchOptions(itemSearch2, openedDocItemState.item2id);

  const itemsOptions: Option[] = useMemo(
    () => allItems.map((i) => ({ value: String(i.itemid), label: (i as any).longname || i.name })),
    [allItems]
  );

  const itemsOptions1: Option[] = useMemo(() => {
    if (itemSearch1.trim().length === 0) {
      const map = new Map<string, Option>();
      [...remoteItemOptions1, ...itemsOptions].forEach((o) =>
        map.set(o.value, o)
      );
      return Array.from(map.values());
    }
    const selectedId = openedDocItemState.itemid
      ? String(openedDocItemState.itemid)
      : null;
    const selectedOpt = selectedId
      ? itemsOptions.find((o) => o.value === selectedId)
      : undefined;
    const merged = selectedOpt
      ? [selectedOpt, ...remoteItemOptions1]
      : remoteItemOptions1;
    const map = new Map<string, Option>();
    merged.forEach((o) => map.set(o.value, o));
    return Array.from(map.values());
  }, [itemSearch1, itemsOptions, remoteItemOptions1, openedDocItemState.itemid]);

  const itemsOptions2: Option[] = useMemo(() => {
    if (itemSearch2.trim().length === 0) {
      const map = new Map<string, Option>();
      [...remoteItemOptions2, ...itemsOptions].forEach((o) =>
        map.set(o.value, o)
      );
      return Array.from(map.values());
    }
    const selectedId = openedDocItemState.item2id
      ? String(openedDocItemState.item2id)
      : null;
    const selectedOpt = selectedId
      ? itemsOptions.find((o) => o.value === selectedId)
      : undefined;
    const merged = selectedOpt
      ? [selectedOpt, ...remoteItemOptions2]
      : remoteItemOptions2;
    const map = new Map<string, Option>();
    merged.forEach((o) => map.set(o.value, o));
    return Array.from(map.values());
  }, [itemSearch2, itemsOptions, remoteItemOptions2, openedDocItemState.item2id]);

  const measureUnitsOptions: Option[] = useMemo(
    () => measureUnits.map((i) => ({ value: i.measureid, label: i.name })),
    [measureUnits]
  );

  const itemVal: Option | undefined = useMemo(
    () =>
      itemsOptions.find((i) => +i.value === Number(openedDocItemState.itemid)),
    [itemsOptions, openedDocItemState.itemid]
  );

  const item2Val: Option | undefined = useMemo(
    () =>
      itemsOptions.find((i) => +i.value === Number(openedDocItemState.item2id)),
    [itemsOptions, openedDocItemState.item2id]
  );

  const measureUnitVal: Option | undefined = useMemo(
    () =>
      measureUnitsOptions.find(
        (i) => +i.value === Number(openedDocItemState.measureid)
      ),
    [measureUnitsOptions, openedDocItemState.measureid]
  );

  const measureUni2tVal: Option | undefined = useMemo(
    () =>
      measureUnitsOptions.find(
        (i) => +i.value === Number(openedDocItemState.measure2id)
      ),
    [measureUnitsOptions, openedDocItemState.measure2id]
  );

  const handleItem = (val) => {
    const selectedItem =
      allItems.find((i) => String(i.itemid) === String(val?.value)) ??
      remoteItems1.find((i) => String(i.itemid) === String(val?.value));
    setOpenedDocumentState({
      itemid: val?.value,
      measureid:
        selectedItem?.measureid || null,
    } as any);
  };

  const handleItem2 = (val) => {
    const selectedItem =
      allItems.find((i) => String(i.itemid) === String(val?.value)) ??
      remoteItems2.find((i) => String(i.itemid) === String(val?.value));
    setOpenedDocumentState({
      item2id: val?.value,
      measure2id:
        selectedItem?.measureid || null,
    } as any);
  };

  const handleAmount = (ev) => {
    const objToUpdate: any = { amount: ev.target.value };

    setOpenedDocumentState(objToUpdate as any);
  };

  return (
    <div className={styles.wrapper}>
      <VerticalSpace size={20} />
      <ElementContainer>
        <NamedSelect
          options={itemsOptions1}
          name={"Из товара"}
          nameClassName={styles.inputName}
          className={styles.itemSelect}
          value={itemVal}
          onChange={handleItem}
          placeholder={"Выберите товар"}
          isSearchable
          isLoading={isItemsLoading1}
          onInputChange={setItemSearch1}
          filterOption={null}
        />
      </ElementContainer>
      <div className={styles.inputsRow}>
        <ElementContainer>
          <NamedSelect
            options={measureUnitsOptions}
            name={"Ед. изм"}
            nameClassName={styles.inputName}
            value={measureUnitVal}
            onChange={(val) =>
              setOpenedDocumentState({ measureid: val?.value } as any)
            }
            placeholder={"Выберите ед. изм"}
          />
        </ElementContainer>
      </div>
      <VerticalSpace size={40} />
      <Divider />
      <VerticalSpace size={20} />
      <ElementContainer>
        <NamedSelect
          options={itemsOptions2}
          name={"В товар"}
          nameClassName={styles.inputName}
          className={styles.itemSelect}
          value={item2Val}
          onChange={handleItem2}
          placeholder={"Выберите товар"}
          isSearchable
          isLoading={isItemsLoading2}
          onInputChange={setItemSearch2}
          filterOption={null}
        />
      </ElementContainer>
      <div className={styles.inputsRow}>
        <ElementContainer>
          <NamedSelect
            options={measureUnitsOptions}
            name={"Ед. изм"}
            nameClassName={styles.inputName}
            value={measureUni2tVal}
            onChange={(val) =>
              setOpenedDocumentState({ measure2id: val?.value } as any)
            }
            placeholder={"Выберите ед. изм"}
          />
        </ElementContainer>
      </div>
      <VerticalSpace size={40} />
      <Divider />
      <VerticalSpace size={40} />

      <ElementContainer>
        <NamedInput
          label={"Кол-во"}
          type={"number"}
          labelClassName={classNames(
            styles.inputName,
            styles.inputNameMarginRight
          )}
          isLabelRegular
          value={openedDocItemState.amount || ""}
          onChange={handleAmount}
        />
      </ElementContainer>

      <VerticalSpace size={100} />
    </div>
  );
};
